import { useSites } from '../../hooks/useSites';
import { useDailyOpsRuns } from '../../hooks/useDailyOpsRuns';
import { DailyOpsRunRow } from './DailyOpsRunRow';
import { FileText } from 'lucide-react';

export function DailyOpsRunsTable() {
  const sitesQuery = useSites();
  const sites = sitesQuery.data?.sites ?? [];
  const showSiteColumn = sites.length !== 1;
  const sitesById = new Map(sites.map((s) => [s.site_id, s]));

  const { pages, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, error } = useDailyOpsRuns();

  if (isLoading) return <RunsTableSkeleton />;
  if (error) return <RunsTableError message={error.message} />;

  const allRuns = pages.flatMap((p) => p.runs);
  if (allRuns.length === 0) return <RunsTableEmpty />;

  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-ems-text-muted mb-2">
        Past Reports
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b-2 border-gray-200 text-xs text-ems-text-muted">
            <th scope="col" className="text-left py-2 px-1 font-semibold">Date</th>
            {showSiteColumn && <th scope="col" className="text-left py-2 px-1 font-semibold">Site</th>}
            <th scope="col" className="text-left py-2 px-1 font-semibold">Status</th>
            <th scope="col" className="text-right py-2 px-1 font-semibold">Action</th>
          </tr>
        </thead>
        <tbody>
          {allRuns.map((run) => (
            <DailyOpsRunRow
              key={run.run_id}
              run={run}
              showSite={showSiteColumn}
              siteName={sitesById.get(run.site_id)?.name ?? '—'}
            />
          ))}
        </tbody>
      </table>
      {hasNextPage && (
        <div className="mt-3 text-center">
          <button
            type="button"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="px-4 py-1.5 bg-white border border-gray-300 rounded text-xs text-ems-text-primary hover:bg-ems-bg-subtle disabled:opacity-50"
          >
            {isFetchingNextPage ? 'Loading…' : `Load more (showing ${allRuns.length})`}
          </button>
        </div>
      )}
    </div>
  );
}

function RunsTableSkeleton() {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-ems-text-muted mb-2">Past Reports</div>
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            data-testid="runs-row-skeleton"
            className="h-10 bg-gray-100 rounded animate-pulse"
          />
        ))}
      </div>
    </div>
  );
}

function RunsTableEmpty() {
  return (
    <div className="text-center py-8">
      <FileText className="w-12 h-12 mx-auto text-gray-400 mb-2" aria-hidden />
      <div className="text-sm font-semibold text-ems-text-primary mb-1">No reports generated yet</div>
      <div className="text-xs text-ems-text-muted">
        The first report will appear after tomorrow morning's 06:15 site-local run.
      </div>
    </div>
  );
}

function RunsTableError({ message }: { message: string }) {
  return (
    <div className="text-center py-6 text-sm text-red-600" role="alert">
      Couldn't load reports. {message}
    </div>
  );
}
